import { useState } from 'react';

import { ChevronsLeft, ChevronsRight, History } from 'lucide-react';

import { DrawerHeaderButton } from 'design-system';
import type { IntelSummary } from 'design-system';

import { cn } from '@/lib/utils';

import AskGXHeader from './AskGXHeader';
import Chat from './Chat/Chat';
import ChatHistory from './ChatHistory/ChatHistory';
import { ChatProvider } from './ChatProvider';
import InputDisclaimer from './InputDisclaimer/InputDisclaimer';
import MessageInput from './MessageInput/MessageInput';
import SignalContextBanner from './SignalContextBanner/SignalContextBanner';
import { useAskGXContent } from './useAskGXContent';

const HEADER_BUTTON_CLASSNAME =
  'size-6 self-auto rounded-[3px] border border-[#3d3f4c] bg-transparent text-[#66677a] hover:border-[#9fa0ad] hover:bg-transparent hover:text-[#f3f2ed] focus-visible:ring-[#8c7cf0] [&_svg]:size-3.5';

const AskGXPanelContent = ({
  selectedSignal,
  isExpanded,
  onToggleExpand
}: {
  selectedSignal?: IntelSummary | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) => {
  const { anchorEl, setAnchorEl, historyBtnRef, handleHistoryClick } = useAskGXContent();

  return (
    <div className='flex h-full min-h-0 w-full flex-col overflow-hidden border-l border-[#26272f] bg-[#0a0a0f]'>
      <AskGXHeader
        actions={
          <>
            <DrawerHeaderButton
              className={HEADER_BUTTON_CLASSNAME}
              label={isExpanded ? 'Collapse' : 'Expand'}
              onClick={onToggleExpand}
            >
              {isExpanded ? (
                <ChevronsRight aria-hidden='true' />
              ) : (
                <ChevronsLeft aria-hidden='true' />
              )}
            </DrawerHeaderButton>
            <DrawerHeaderButton
              ref={historyBtnRef}
              active={Boolean(anchorEl)}
              className={HEADER_BUTTON_CLASSNAME}
              label='Conversation history'
              onClick={handleHistoryClick}
            >
              <History aria-hidden='true' />
            </DrawerHeaderButton>
          </>
        }
      />
      {selectedSignal && <SignalContextBanner signal={selectedSignal} />}
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
        <Chat hideEmptyState={Boolean(selectedSignal)} />
        <MessageInput maxRows={3} />
        <InputDisclaimer />
        <ChatHistory
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      </div>
    </div>
  );
};

/**
 * The Ask-GX assistant rendered as a persistent, docked panel on routes that
 * reserve a right-hand column for the assistant (see `AskGXDockedLayout`).
 * When `selectedSignal` is given (a selected Morning Intel signal), shows its
 * context above the normal chat. Owns its own width so the header's Expand
 * button can widen the panel without the hosting layout knowing about it.
 */
const AskGXPanel = ({ selectedSignal }: { selectedSignal?: IntelSummary | null }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={cn('h-full shrink-0', isExpanded ? 'w-[min(42.5rem,50vw)]' : 'w-[21.25rem]')}>
      <ChatProvider open>
        <AskGXPanelContent
          isExpanded={isExpanded}
          selectedSignal={selectedSignal}
          onToggleExpand={() => setIsExpanded(value => !value)}
        />
      </ChatProvider>
    </div>
  );
};

export default AskGXPanel;
